import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
// import moment from 'moment';
import locationApi from 'api/locationApi';

import SelectValidation from 'components/App/SelectValidation';
import AppLoading from 'components/App/Loading';
import Card from 'components/App/Card';
import OrderChart from 'components/Charts/OrderChart';
import CustomActiveShapePieChart from 'components/Charts/PieChart';
import { currencyFormat, formatNumber } from 'utils/currencyFormat';

import { ANALYTIC_TYPE } from 'constants/analytics';
import INITIAL_VALUE from 'constants/initialValue';
import { getParamsFormObject } from 'utils';

import styles from './GuestInsight.module.scss';

const OPTION_FILTER_BY_TIME = [
  {
    label: '365 ngày qua',
    value: 365,
  },
  {
    label: '90 ngày qua',
    value: 90,
  },
  {
    label: '30 ngày qua',
    value: 30,
  },
  {
    label: '14 ngày qua',
    value: 14,
  },
  {
    label: '7 ngày qua',
    value: 7,
  },
];

const OPTION_DISPLAY = [
  {
    label: 'Tỉ lệ trung bình ngày',
    value: 'AVG_DAILY',
  },
  {
    label: 'Tỉ lệ đặt phòng',
    value: 'BOOKING_RATE',
  },
];

const OPTION_COMPARE = [
  {
    label: 'Cùng phân khúc',
    value: 'SAME_SEGMENT',
  },
  {
    label: 'Thị trường',
    value: 'MARKET',
  },
  {
    label: 'Năm trước',
    value: 'LAST_YEAR',
  },
];

const LEGEND_ITEMS_2 = [
  { label: 'Huỷ phòng', color: '#F46A6A' },
  { label: 'Vắng mặt', color: '#A6B0CF' },
  { label: 'Thành công', color: '#2CC4A2' },
];

const LEGEND_ITEMS_3 = [
  { label: 'Thành viên', color: '#165DFF' },
  { label: 'Du khách', color: '#14C9C9' },
  { label: 'Doanh nghiệp', color: '#2CC4A2' },
  { label: 'Đại lý', color: '#F7BA1E' },
];

const parsePercent = (val, defaultVal) => {
  if (val === undefined || val === null) return defaultVal;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(val);
  return Number.isNaN(parsed) ? defaultVal : parsed;
};

function GuestInsight(props) {
  const {
    isLoading,
    data,
  } = props;

  const [chartData, setChartData] = React.useState([]);
  const [stayNightsChartData, setStayNightsChartData] = React.useState([]);
  const [bookingStatusRatio, setBookingStatusRatio] = React.useState(null);
  const [
    revenueDistributionAccountLevels,
    setRevenueDistributionAccountLevels,
  ] = React.useState(null);
  const [calculatedAt, setCalculatedAt] = React.useState('');
  const [totalRoomNights, setTotalRoomNights] = React.useState(undefined);

  const groups = bookingStatusRatio?.data?.groups || [];
  const cancelGroup = groups.find((g) => g.key === 'CANCELLED');
  const absentGroup = groups.find((g) => g.key === 'ABSENT');
  const successGroup = groups.find((g) => g.key === 'SUCCESS');

  const cancelRate = bookingStatusRatio
    ? parsePercent(cancelGroup?.bookingRatio, 0)
    : parsePercent(
      data?.cancelRate ?? data?.cancellationRate ?? data?.cancelPercent ?? data?.cancelled,
      0,
    );

  const noShowRate = bookingStatusRatio
    ? parsePercent(absentGroup?.bookingRatio, 0)
    : parsePercent(
      data?.noShowRate ?? data?.noShowPercent ?? data?.noShow,
      0,
    );

  const successRate = bookingStatusRatio
    ? parsePercent(successGroup?.bookingRatio, 0)
    : parsePercent(
      data?.successRate ?? data?.successPercent ?? data?.completedRate ?? data?.success,
      0,
    );

  const history = useHistory();
  const { search } = useLocation();
  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
      days: params.get('days') || 365,
      type: params.get('type') || ANALYTIC_TYPE.GUEST_INSIGHT,
    };
  }, [search]);

  const [displayType, setDisplayType] = React.useState(OPTION_DISPLAY[0].value);
  const [compareWith, setCompareWith] = React.useState(OPTION_COMPARE[0].value);

  const onChangeFilterByTime = React.useCallback((e) => {
    history.push({
      search: getParamsFormObject({
        ...searchQuery,
        days: e.value,
      }),
    });
  }, [history, searchQuery]);

  const onChangeDisplay = React.useCallback((e) => {
    setDisplayType(e.value);
  }, []);

  const onChangeCompare = React.useCallback((e) => {
    setCompareWith(e.value);
  }, []);

  React.useEffect(() => {
    const fetchChartsData = async () => {
      const { place } = searchQuery;
      const { days } = searchQuery;

      if (!place) {
        setChartData([]);
        setStayNightsChartData([]);
        setBookingStatusRatio(null);
        setRevenueDistributionAccountLevels(null);
        setCalculatedAt('');
        setTotalRoomNights(undefined);
        return;
      }

      // Fetch Advance Booking Charts
      try {
        const response = await locationApi.getAdvanceBookingCharts({
          place,
          days: +days || 365,
          compareWith,
        });

        const payload = response?.payload || {};
        const myGroups = payload?.data?.groups || [];
        const compareGroups = payload?.compareData?.groups || [];
        const compareMap = new Map(compareGroups.map((item) => [item?.key, item]));

        const mapped = myGroups.map((item, idx) => {
          const compareItem = compareMap.get(item?.key);
          return {
            name: item?.label || item?.key || `Nhóm ${idx + 1}`,
            myBooking: Number(item?.bookingRatio) || 0,
            peerBooking: Number(compareItem?.bookingRatio) || 0,
            myPrice: Number(item?.averagePriceRatio) || 0,
            peerPrice: Number(compareItem?.averagePriceRatio) || 0,
          };
        });

        setChartData(mapped);
        setCalculatedAt(payload?.compareData?.calculatedAt || payload?.data?.calculatedAt || '');
        setTotalRoomNights(payload?.compareData?.totalRoomNights ?? payload?.data?.totalRoomNights);
      } catch {
        setChartData([]);
        setCalculatedAt('');
        setTotalRoomNights(undefined);
      }

      // Fetch Stay Nights Charts
      try {
        const response = await locationApi.getStayNightsCharts({
          place,
          days: +days || 365,
          compareWith,
        });

        const payload = response?.payload || {};
        const myGroups = payload?.data?.groups || [];
        const compareGroups = payload?.compareData?.groups || [];
        const compareMap = new Map(compareGroups.map((item) => [item?.key, item]));

        const mapped = myGroups.map((item, idx) => {
          const compareItem = compareMap.get(item?.key);
          return {
            name: item?.label || item?.key || `Nhóm ${idx + 1}`,
            myBooking: Number(item?.bookingRatio) || 0,
            peerBooking: Number(compareItem?.bookingRatio) || 0,
            myPrice: Number(item?.averagePriceRatio) || 0,
            peerPrice: Number(compareItem?.averagePriceRatio) || 0,
          };
        });

        setStayNightsChartData(mapped);
      } catch {
        setStayNightsChartData([]);
      }

      // Fetch Booking Status Ratio
      try {
        const response = await locationApi.getBookingStatusRatio({
          place,
          days: +days || 365,
          compareWith,
        });
        setBookingStatusRatio(response?.payload || response);
      } catch {
        setBookingStatusRatio(null);
      }

      // Fetch Revenue Distribution Account Levels
      try {
        const response = await locationApi.getRevenueDistributionAccountLevels({
          place,
          days: +days || 365,
          compareWith,
        });
        setRevenueDistributionAccountLevels(response?.payload || response);
      } catch {
        setRevenueDistributionAccountLevels(null);
      }
    };

    fetchChartsData();
  }, [searchQuery.place, searchQuery.days, compareWith]);

  const apiItems = React.useMemo(() => {
    const rawData = revenueDistributionAccountLevels?.data || revenueDistributionAccountLevels;
    return rawData?.groups || rawData || [];
  }, [revenueDistributionAccountLevels]);

  const ACCOUNT_LEVEL_TRANSLATIONS = {
    TOURIST: 'Du khách',
    MEMBER: 'Thành viên',
    AGENT: 'Đại lý',
    ENTERPRISE: 'Doanh nghiệp',
    OTHER: 'Khác',
  };

  const getAccountLabel = (key) => {
    if (!key) return 'Khác';
    const upperKey = String(key).toUpperCase().trim();
    return ACCOUNT_LEVEL_TRANSLATIONS[upperKey] || upperKey;
  };

  const ACCOUNT_LEVEL_COLORS = {
    'Thành viên': '#165DFF',
    'Du khách': '#14C9C9',
    'Doanh nghiệp': '#2CC4A2',
    'Đại lý': '#F7BA1E',
    Khác: '#CED4DA',
  };

  const getAccountColor = (label) => ACCOUNT_LEVEL_COLORS[label] || '#CED4DA';

  const revenuePieData = React.useMemo(() => {
    if (revenueDistributionAccountLevels) {
      return apiItems.map((item) => {
        const name = item.label || getAccountLabel(item.key || item.account || item.name);
        return {
          name,
          value: Number(item.revenue) || 0,
          color: getAccountColor(name),
        };
      });
    }
    return data?.revenuePieData || data?.revenueDistribution || [];
  }, [revenueDistributionAccountLevels, apiItems, data]);

  const nightsPieData = React.useMemo(() => {
    if (revenueDistributionAccountLevels) {
      return apiItems.map((item) => {
        const name = item.label || getAccountLabel(item.key || item.account || item.name);
        return {
          name,
          value: Number(item.roomNights ?? item.nights ?? item.bookings ?? 0),
          color: getAccountColor(name),
        };
      });
    }
    return data?.nightsPieData || data?.nightDistribution || [];
  }, [revenueDistributionAccountLevels, apiItems, data]);

  const accountTableData = React.useMemo(() => {
    if (revenueDistributionAccountLevels) {
      return apiItems.map((item) => {
        const account = item.label || getAccountLabel(item.key || item.account || item.name);
        const revenue = Number(item.revenue) || 0;
        const bookings = Number(item.roomNights ?? item.bookings ?? 0);
        const avgPrice = Number(item.averagePrice ?? item.avgPrice ?? 0);
        return {
          account,
          revenue,
          bookings,
          avgPrice,
        };
      });
    }
    return data?.tableData || data?.accountBreakdown || [];
  }, [revenueDistributionAccountLevels, apiItems, data]);

  const COMPARE_LABELS = React.useMemo(() => ({
    SAME_SEGMENT: 'cùng phân khúc',
    MARKET: 'thị trường',
    LAST_YEAR: 'năm ngoái',
  }), []);

  const compareLabel = React.useMemo(() => COMPARE_LABELS[compareWith] || 'cùng phân khúc', [compareWith, COMPARE_LABELS]);

  const formatDiffText = (myBooking, peerBooking, compareWithVal) => {
    if (!myBooking || !peerBooking) return '';
    const diff = Number((myBooking - peerBooking).toFixed(1));
    const labelVal = COMPARE_LABELS[compareWithVal] || 'năm ngoái';
    if (diff > 0) return `+${formatNumber(diff)}% so với ${labelVal}`;
    if (diff < 0) return `-${formatNumber(Math.abs(diff))}% so với ${labelVal}`;
    return '';
  };

  const topAdvanceBookingItem = React.useMemo(() => {
    if (!chartData || !chartData.length) return null;
    return chartData.reduce((max, item) => (
      item.myBooking > max.myBooking ? item : max
    ), chartData[0]);
  }, [chartData]);

  const topAdvanceBookingPeriod = topAdvanceBookingItem?.name || '';

  const advanceBookingDiffText = React.useMemo(() => {
    if (!topAdvanceBookingItem) return '';
    return formatDiffText(
      topAdvanceBookingItem.myBooking,
      topAdvanceBookingItem.peerBooking,
      compareWith,
    );
  }, [topAdvanceBookingItem, compareWith]);

  const topStayNightsItem = React.useMemo(() => {
    if (!stayNightsChartData || !stayNightsChartData.length) return null;
    return stayNightsChartData.reduce((max, item) => (
      item.myBooking > max.myBooking ? item : max
    ), stayNightsChartData[0]);
  }, [stayNightsChartData]);

  const topStayNightsName = topStayNightsItem?.name || '';

  const stayNightsDiffText = React.useMemo(() => {
    if (!topStayNightsItem) return '';
    return formatDiffText(
      topStayNightsItem.myBooking,
      topStayNightsItem.peerBooking,
      compareWith,
    );
  }, [topStayNightsItem, compareWith]);

  return (
    <div className="">
      <AppLoading loading={isLoading} loaderOnPage />
      <Card className={styles.Components}>
        <div className={styles.Filter}>
          <div className={styles.FilterItem}>
            <span className={styles.Label}>
              Cho xem dữ liệu trong:
            </span>

            <SelectValidation
              onChange={onChangeFilterByTime}
              options={OPTION_FILTER_BY_TIME}
              selectedClassName={styles.Selection}
              value={+searchQuery.days}
            />
          </div>

          <div className={styles.FilterItem}>
            <span className={styles.Label}>
              Hiển thị:
            </span>

            <SelectValidation
              onChange={onChangeDisplay}
              options={OPTION_DISPLAY}
              selectedClassName={styles.Selection}
              value={displayType}
            />
          </div>

          <div className={styles.FilterItem}>
            <span className={styles.Label}>
              So sánh với:
            </span>

            <SelectValidation
              onChange={onChangeCompare}
              options={OPTION_COMPARE}
              selectedClassName={styles.Selection}
              value={compareWith}
            />
          </div>
        </div>
        <div className="">
          {/* filter */}
          <div className={styles.Result}>
            <div className={`${styles.ResultItem} ${styles.PresetTime}`}>
              <p className={`${styles.Text} ${styles.TextTitle}`}>Thời gian đặt trước</p>
              <p className={`${styles.Text} ${styles.TextValue}`}>
                {topAdvanceBookingPeriod || data?.advanceBookingPeriod || '-'}
              </p>
              {advanceBookingDiffText && (
                <p className={`${styles.Text} ${styles.TextDescription}`}>{advanceBookingDiffText}</p>
              )}
            </div>
            <div className={styles.ResultItem}>
              <p className={`${styles.Text} ${styles.TextTitle}`}>Số đêm lưu trú tối thiểu</p>
              <p className={`${styles.Text} ${styles.TextValue}`}>
                {topStayNightsName || data?.minNightsStay || '-'}
              </p>
              {stayNightsDiffText && (
                <p className={`${styles.Text} ${styles.TextDescription}`}>{stayNightsDiffText}</p>
              )}
            </div>
          </div>

          <OrderChart
            chartData={chartData}
            stayNightsChartData={stayNightsChartData}
            calculatedAt={calculatedAt}
            totalRoomNights={totalRoomNights}
            compareLabel={compareLabel}
          />
        </div>
      </Card>

      <Card className={styles.Components}>
        <div className={styles.ProgressBarContainer}>
          <div className={styles.ProgressBar}>
            {cancelRate > 0 && (
              <div
                className={`${styles.Segment} ${styles.CancelSegment}`}
                style={{ width: `${cancelRate}%` }}
              >
                {formatNumber(cancelRate)}
                %
              </div>
            )}
            {noShowRate > 0 && (
              <div
                className={`${styles.Segment} ${styles.AbsentSegment}`}
                style={{ width: `${noShowRate}%` }}
              >
                {formatNumber(noShowRate)}
                %
              </div>
            )}
            {successRate > 0 && (
              <div
                className={`${styles.Segment} ${styles.SuccessSegment}`}
                style={{ width: `${successRate}%` }}
              >
                {formatNumber(successRate)}
                %
              </div>
            )}
          </div>
        </div>

        <div className={styles.legend}>
          {LEGEND_ITEMS_2.map((item) => (
            <div key={item.label} className={styles.item}>
              <div
                className={styles.dot}
                style={{ backgroundColor: item.color }}
              />
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className={styles.Components}>
        <h4 className={styles.H4title}>Phân phối doanh thu theo Tài khoản</h4>
        <div className={styles.PieChartsSection}>
          <div className={styles.PieChartColumn}>
            <CustomActiveShapePieChart data={revenuePieData} />
            <p className={styles.PieChartLabel}>Doanh thu</p>
          </div>
          <div className={styles.PieChartColumn}>
            <CustomActiveShapePieChart data={nightsPieData} />
            <p className={styles.PieChartLabel}>Đêm phòng</p>
          </div>
          <div className={styles.PieChartLegend}>
            {LEGEND_ITEMS_3.map((item) => (
              <div key={item.label} className={styles.LegendItem}>
                <span
                  className={styles.Dot}
                  style={{ backgroundColor: item.color }}
                />
                <span className={styles.Label}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.TableResponsive}>
          <table className={styles.Table}>
            <thead className={styles.TableHeading}>
              <tr className={styles.TableHeadingRow}>
                <th>Tài khoản</th>
                <th>Doanh thu (bằng VND)</th>
                <th>Số lượt đặt phòng</th>
                <th>Giá bình quân</th>
              </tr>
            </thead>
            <tbody className={styles.TableBody}>
              {accountTableData.map((item, index) => (
                <tr key={item.account || item._id || index} className={styles.TableBodyRow}>
                  <td>{item.account}</td>
                  <td>
                    {currencyFormat({
                      value: item.revenue,
                    })}
                  </td>
                  <td>{formatNumber(item.bookings)}</td>
                  <td>
                    {currencyFormat({
                      value: item.avgPrice,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

GuestInsight.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.instanceOf(Object),
};

GuestInsight.defaultProps = {
  data: INITIAL_VALUE.OBJECT,
};

export default React.memo(GuestInsight);
